// Messagerie (internal chat) API client.
// Talks to the hexaflow backend /messagerie endpoints. Mutations + pagination
// go through HTTP; real-time delivery is handled by Firestore onSnapshot in
// the messagerie store. Auth uses the shared Firebase Bearer-token helper.

import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken";

async function request(method, prefix, path, body) {
  const token = await getOrRefreshFirebaseToken();
  const res = await fetch(`${prefix}messagerie${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`messagerie ${method} ${path} -> ${res.status} ${text}`);
  }
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

const messagerieApi = {
  // --- Discussions ---
  listDiscussions: (prefix, { tab, limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams();
    if (tab) params.set("tab", tab);
    params.set("limit", limit);
    params.set("offset", offset);
    return request("GET", prefix, `/discussions?${params.toString()}`);
  },
  createDiscussion: (prefix, data) =>
    request("POST", prefix, `/discussions`, data),
  getDiscussion: (prefix, id) => request("GET", prefix, `/discussions/${id}`),
  updateDiscussion: (prefix, id, data) =>
    request("PATCH", prefix, `/discussions/${id}`, data),
  deleteDiscussion: (prefix, id) =>
    request("DELETE", prefix, `/discussions/${id}`),

  // --- Members ---
  addMembers: (prefix, id, userIds) =>
    request("POST", prefix, `/discussions/${id}/members`, { user_ids: userIds }),
  removeMember: (prefix, id, targetUserId) =>
    request("DELETE", prefix, `/discussions/${id}/members/${targetUserId}`),
  mute: (prefix, id, mute) =>
    request("POST", prefix, `/discussions/${id}/mute`, { mute }),
  follow: (prefix, id) => request("POST", prefix, `/discussions/${id}/follow`),
  unfollow: (prefix, id) =>
    request("POST", prefix, `/discussions/${id}/unfollow`),

  // --- Messages ---
  listMessages: (prefix, id, { limit = 50, before } = {}) => {
    const params = new URLSearchParams();
    params.set("limit", limit);
    if (before) params.set("before", before);
    return request(
      "GET",
      prefix,
      `/discussions/${id}/messages?${params.toString()}`
    );
  },
  searchMessages: (prefix, id, q, limit = 50) => {
    const params = new URLSearchParams();
    params.set("q", q);
    params.set("limit", limit);
    return request(
      "GET",
      prefix,
      `/discussions/${id}/messages/search?${params.toString()}`
    );
  },
  sendMessage: (prefix, id, data) =>
    request("POST", prefix, `/discussions/${id}/messages`, data),
  updateMessage: (prefix, id, messageId, text) =>
    request("PATCH", prefix, `/discussions/${id}/messages/${messageId}`, {
      text,
    }),
  deleteMessage: (prefix, id, messageId) =>
    request("DELETE", prefix, `/discussions/${id}/messages/${messageId}`),
  toggleReaction: (prefix, id, messageId, emoji) =>
    request(
      "POST",
      prefix,
      `/discussions/${id}/messages/${messageId}/reactions`,
      { emoji }
    ),
  pinMessage: (prefix, id, messageId) =>
    request("POST", prefix, `/discussions/${id}/messages/${messageId}/pin`),
  unpinMessage: (prefix, id, messageId) =>
    request("POST", prefix, `/discussions/${id}/messages/${messageId}/unpin`),

  // --- Polls (sondages) ---
  // Toggles the user's vote on a single option (race-free per-option toggle).
  votePoll: (prefix, id, messageId, optionId) =>
    request("POST", prefix, `/discussions/${id}/messages/${messageId}/vote`, {
      option_id: optionId,
    }),
  closePoll: (prefix, id, messageId) =>
    request("POST", prefix, `/discussions/${id}/messages/${messageId}/poll/close`),

  // --- Read receipts ---
  markAsRead: (prefix, id, lastReadMessageId) =>
    request("POST", prefix, `/discussions/${id}/read`, {
      last_read_message_id: lastReadMessageId,
    }),
  getReadReceipts: (prefix, id) =>
    request("GET", prefix, `/discussions/${id}/read-receipts`),
  getUnreadCounts: (prefix) => request("GET", prefix, `/unread-counts`),

  // --- File upload (multipart, proxied through the backend) ---
  // Used for small files (≤ ~30MB): the backend can also render PDF previews.
  // XHR (not fetch) so we get an upload-progress fraction for the UI bar.
  uploadAttachment: (prefix, id, file, onProgress) =>
    getOrRefreshFirebaseToken().then(
      (token) =>
        new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${prefix}messagerie/discussions/${id}/upload`);
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          if (onProgress) {
            xhr.upload.onprogress = (e) => {
              if (e.lengthComputable && e.total > 0) {
                onProgress(Math.max(0, Math.min(1, e.loaded / e.total)));
              }
            };
          }
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch {
                reject(new Error("upload -> réponse illisible"));
              }
            } else {
              reject(new Error(`upload -> ${xhr.status} ${xhr.responseText}`));
            }
          };
          xhr.onerror = () => reject(new Error("upload -> erreur réseau"));
          const form = new FormData();
          form.append("file", file);
          xhr.send(form);
        })
    ),

  // --- Direct-to-GCS upload (large files, e.g. videos) ---
  // Ask the backend for a signed PUT URL, then upload the bytes straight to GCS
  // so the request never hits Cloud Run's 32 MiB HTTP/1 body limit.
  getSignedUpload: (prefix, id, meta) =>
    request("POST", prefix, `/discussions/${id}/signed-upload`, meta),

  putToSignedUrl: (uploadUrl, file, contentType, onProgress) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      // Must match the Content-Type the URL was signed with.
      xhr.setRequestHeader("Content-Type", contentType);
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) {
            onProgress(Math.max(0, Math.min(1, e.loaded / e.total)));
          }
        };
      }
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`gcs put -> ${xhr.status} ${xhr.responseText}`));
      xhr.onerror = () => reject(new Error("gcs put -> erreur réseau"));
      xhr.send(file);
    }),
};

export default messagerieApi;
